import { useApi } from '@classgo/app-kit';
import type { AlbertDraftKind, AlbertDraftResult } from '@classgo/shared';
import { Button, Field, Input, Select, useToast } from '@classgo/ui';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { albertApi } from '../../albert/api';
import { useT } from '../../i18n';
import { RefusalNotice } from './refusal-notice';
import { bulletsOf } from './support';
import type { PanelProps } from './types';

const KINDS: AlbertDraftKind[] = ['observation', 'family-message'];
const TONES = ['cercano', 'formal'] as const;

const TEXTAREA =
  'w-full rounded-control border border-gray-300 p-3 text-sm leading-6 text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

/**
 * Drafts from bullet points: the kind of text, an optional first name, the tone and one bullet
 * per line; the answer lands in an editable textarea with "Copiar texto". Nothing is stored:
 * the teacher pastes it where it goes.
 */
export const DraftPanel = ({ pair, onQuota }: Pick<PanelProps, 'pair' | 'onQuota'>) => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const bulletsId = useId();
  const resultId = useId();
  const [kind, setKind] = useState<AlbertDraftKind>('observation');
  const [tone, setTone] = useState<(typeof TONES)[number]>('cercano');
  const [firstName, setFirstName] = useState('');
  const [bulletsText, setBulletsText] = useState('');
  const [result, setResult] = useState<AlbertDraftResult | null>(null);
  const [text, setText] = useState('');
  const bullets = bulletsOf(bulletsText);
  const valid = bullets.length > 0 && bullets.every((line) => line.length >= 2);

  const write = useMutation({
    mutationFn: () =>
      albertApi(api).writeDraft({
        groupId: pair.groupId,
        subjectId: pair.subjectId,
        kind,
        tone,
        bullets,
        ...(firstName.trim() ? { studentFirstName: firstName.trim() } : {}),
      }),
    onSuccess: (answer) => {
      onQuota(answer.quota);
      setResult(answer);
      setText(answer.text);
    },
  });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.show({ tone: 'success', title: t('albert.draft.copied') });
    } catch {
      toast.show({ tone: 'error', title: t('albert.draft.copyFailed') });
    }
  };

  const working = write.isPending;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Select
          label={t('albert.draft.kind')}
          value={kind}
          onChange={(event) => setKind(event.target.value as AlbertDraftKind)}
          options={KINDS.map((value) => ({ value, label: t(`albert.draft.kind.${value}`) }))}
          disabled={working}
        />
        <Select
          label={t('albert.draft.tone')}
          value={tone}
          onChange={(event) => setTone(event.target.value as (typeof TONES)[number])}
          options={TONES.map((value) => ({ value, label: t(`albert.draft.tone.${value}`) }))}
          disabled={working}
        />
        <Input
          label={t('albert.draft.student')}
          help={t('albert.draft.student.help')}
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          maxLength={40}
          disabled={working}
        />
      </div>
      <Field id={bulletsId} label={t('albert.draft.bullets')} help={t('albert.draft.bullets.help')}>
        <textarea
          id={bulletsId}
          className={TEXTAREA}
          rows={5}
          value={bulletsText}
          onChange={(event) => setBulletsText(event.target.value)}
          disabled={working}
          aria-describedby={`${bulletsId}-help`}
        />
      </Field>
      <div className="flex justify-end">
        <Button loading={working} disabled={!valid} onClick={() => write.mutate()}>
          {t('albert.draft.generate')}
        </Button>
      </div>
      {working ? (
        <p role="status" className="text-sm text-gray-600">
          {t('albert.draft.working')}
        </p>
      ) : null}
      {write.isError && !working ? (
        <RefusalNotice error={write.error} retrying={working} onRetry={() => write.mutate()} />
      ) : null}
      {result ? (
        <div className="space-y-2">
          <Field
            id={resultId}
            label={t('albert.draft.result')}
            help={t('albert.draft.result.help')}
          >
            <textarea
              id={resultId}
              className={TEXTAREA}
              rows={7}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </Field>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">
              {t('albert.usage', {
                tokens: `${result.usage.inputTokens} + ${result.usage.outputTokens}`,
                latency: `${(result.usage.latencyMs / 1000).toFixed(1)} s`,
              })}
            </p>
            <Button variant="secondary" onClick={() => void copy()}>
              {t('albert.draft.copy')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
